import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 电子电路 - 横屏「电路原理」风格
 *
 * 核心亮点：
 * - SVG 电路图与动画信号流（沿路径移动的点）
 * - 元件符号（电阻锯齿、电容平行线、LED 三角形、电池）
 * - 电压/电流动画数值
 * - 元件信息卡片
 * - 欧姆定律公式展示
 *
 * 尺寸：1920x1080 横屏
 */

interface CircuitComponent {
  name: string;
  symbol: string;
  value: string;
  unit: string;
  description: string;
}

interface Measurement {
  label: string;
  value: string;
  unit: string;
  color: string;
}

interface ElectronicsCircuitTemplateProps {
  topic: string;
  subtitle: string;
  components: CircuitComponent[];
  circuitPath: string;
  formula: string;
  measurements: Measurement[];
  keyFact: string;
  tips: string[];
}

const DEFAULT_PROPS: ElectronicsCircuitTemplateProps = {
  topic: 'LED 基础电路原理',
  subtitle: "Ohm's Law & LED Circuit Design",
  components: [
    { name: '电池', symbol: 'battery', value: '3', unit: 'V', description: '直流电源，提供电压' },
    { name: '电阻', symbol: 'resistor', value: '330', unit: 'Ω', description: '限流电阻，保护 LED' },
    { name: 'LED', symbol: 'led', value: '2', unit: 'V', description: '发光二极管，正向压降约 2V' },
  ],
  circuitPath: 'M 100 400 L 200 400 L 250 400 L 350 400 L 400 400 L 500 400 L 550 400 L 650 400 L 700 400',
  formula: 'V = IR',
  measurements: [
    { label: '电源电压', value: '3.0', unit: 'V', color: '#22c55e' },
    { label: '回路电流', value: '3.0', unit: 'mA', color: '#06b6d4' },
    { label: '电阻压降', value: '1.0', unit: 'V', color: '#f59e0b' },
    { label: 'LED 压降', value: '2.0', unit: 'V', color: '#ef4444' },
  ],
  keyFact: 'LED 需要串联限流电阻，否则会因电流过大而烧毁。常用公式：R = (V电源 - V_LED) / I_LED',
  tips: [
    '红色/黄色 LED 正向压降约 1.8-2.2V',
    '蓝色/白色 LED 正向压降约 3.0-3.6V',
    '一般 LED 工作电流 5-20mA',
  ],
};

export const ElectronicsCircuitTemplate: React.FC<Partial<ElectronicsCircuitTemplateProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerProgress = spring({ frame, fps, config: { damping: 18, stiffness: 90 } });

  const circuitAppear = interpolate(frame, [20, 70], [0, 1], { extrapolateRight: 'clamp' });
  const signalProgress = (frame * 0.8) % 100;
  const dotOffset = interpolate(signalProgress, [0, 100], [0, 600], { extrapolateRight: 'clamp' });

  const formulaStart = 80;
  const formulaOpacity = interpolate(frame, [formulaStart, formulaStart + 30], [0, 1], { extrapolateRight: 'clamp' });
  const formulaScale = spring({ frame: frame - formulaStart, fps, from: 0.9, to: 1, config: { damping: 14, stiffness: 100 } });

  const compStart = 100;
  const measStart = 180;
  const tipStart = 260;
  const factStart = 320;
  const factOpacity = interpolate(frame, [factStart, factStart + 25], [0, 1], { extrapolateRight: 'clamp' });

  // Circuit path for SVG - simplified LED circuit
  const pathLen = 600;
  const signalDotX = 100 + (dotOffset / pathLen) * 600;

  return (
    <AbsoluteFill
      style={{
        background: '#0a1628',
        fontFamily: '-apple-system, "PingFang SC", "Inter", sans-serif',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {/* PCB grid background */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'linear-gradient(rgba(34,197,94,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(34,197,94,0.03) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }}
      />

      {/* Header */}
      <div style={{ position: 'absolute', top: 28, left: 50, opacity: headerProgress, display: 'flex', alignItems: 'baseline', gap: 16 }}>
        <div style={{ padding: '6px 18px', background: '#22c55e18', border: '1px solid #22c55e40', borderRadius: 10, fontSize: 24, color: '#22c55e', fontWeight: 700, flexShrink: 0 }}>
          ⚡ 电子电路
        </div>
        <h1 style={{ fontSize: 40, fontWeight: 900, margin: 0, flexShrink: 0 }}>{p.topic}</h1>
        <p style={{ fontSize: 26, color: 'rgba(255,255,255,0.4)', margin: 0 }}>{p.subtitle}</p>
      </div>

      {/* ========== CIRCUIT DIAGRAM ========== */}
      <svg style={{ position: 'absolute', left: 60, top: 180 }} width="640" height="380" viewBox="0 0 640 380" opacity={circuitAppear}>
        <defs>
          <marker id="arrowSignal" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
            <polygon points="0 0, 8 3, 0 6" fill="#22c55e" />
          </marker>
        </defs>

        {/* Circuit traces (green PCB style) */}
        <path
          d="M 80 200 L 180 200 L 180 120 L 460 120 L 460 200 L 560 200"
          fill="none"
          stroke="#22c55e"
          strokeWidth="4"
          strokeLinecap="round"
          opacity="0.9"
        />
        <path
          d="M 80 200 L 80 280 L 560 280 L 560 200"
          fill="none"
          stroke="#22c55e"
          strokeWidth="4"
          strokeLinecap="round"
          opacity="0.9"
        />
        <path d="M 80 200 L 80 240 L 560 240 L 560 200" fill="none" stroke="#22c55e" strokeWidth="2" strokeDasharray="6 4" opacity="0.4" />

        {/* Battery symbol */}
        <g transform="translate(100, 160)">
          <line x1="0" y1="40" x2="0" y2="0" stroke="#06b6d4" strokeWidth="3" />
          <line x1="0" y1="40" x2="40" y2="40" stroke="#06b6d4" strokeWidth="3" />
          <line x1="40" y1="40" x2="40" y2="0" stroke="#06b6d4" strokeWidth="3" />
          <line x1="20" y1="25" x2="20" y2="15" stroke="#06b6d4" strokeWidth="2" />
          <line x1="20" y1="45" x2="20" y2="35" stroke="#06b6d4" strokeWidth="2" />
          <text x="50" y="25" fill="#06b6d4" fontSize="20" fontWeight="700">3V</text>
        </g>

        {/* Resistor symbol (zigzag) */}
        <g transform="translate(220, 95)">
          <path
            d="M 0 25 L 15 25 L 25 5 L 35 45 L 45 25 L 60 25"
            fill="none"
            stroke="#f59e0b"
            strokeWidth="3"
            strokeLinecap="round"
          />
          <text x="70" y="18" fill="#f59e0b" fontSize="20" fontWeight="700">330Ω</text>
        </g>

        {/* LED symbol (triangle + arrow) */}
        <g transform="translate(380, 95)">
          <path d="M 0 25 L 40 5 L 40 45 Z" fill="none" stroke="#ef4444" strokeWidth="3" />
          <line x1="45" y1="25" x2="55" y2="25" stroke="#ef4444" strokeWidth="2" />
          <line x1="50" y1="20" x2="50" y2="30" stroke="#ef4444" strokeWidth="3" />
          <line x1="48" y1="22" x2="52" y2="28" stroke="#ef4444" strokeWidth="1.5" />
          <text x="65" y="18" fill="#ef4444" fontSize="20" fontWeight="700">LED</text>
        </g>

        {/* Animated signal flow dots */}
        <circle cx={80 + (dotOffset % 480)} cy={200} r="8" fill="#22c55e" opacity={0.7 + 0.3 * Math.sin(frame * 0.15)} />
        <circle cx={80 + ((dotOffset + 150) % 480)} cy={200} r="6" fill="#22c55e" opacity="0.6" />
        <circle cx={80 + ((dotOffset + 300) % 480)} cy={200} r="6" fill="#22c55e" opacity="0.6" />
      </svg>

      {/* ========== FORMULA ========== */}
      <div
        style={{
          position: 'absolute',
          left: 130,
          top: 580,
          opacity: formulaOpacity,
          transform: `scale(${formulaScale})`,
        }}
      >
        <div
          style={{
            display: 'inline-block',
            padding: '20px 36px',
            background: 'rgba(34,197,94,0.08)',
            border: '2px solid rgba(34,197,94,0.4)',
            borderRadius: 16,
          }}
        >
          <div style={{ fontSize: 24, color: 'rgba(255,255,255,0.5)', marginBottom: 8, letterSpacing: '0.1em' }}>欧姆定律</div>
          <div
            style={{
              fontSize: 48,
              fontFamily: '"Times New Roman", Georgia, serif',
              fontWeight: 800,
              color: '#22c55e',
              textShadow: '0 0 30px rgba(34,197,94,0.4)',
            }}
          >
            {p.formula}
          </div>
          <div style={{ fontSize: 24, color: 'rgba(255,255,255,0.5)', marginTop: 8 }}>电压 = 电流 × 电阻</div>
        </div>
      </div>

      {/* ========== RIGHT PANEL ========== */}
      <div style={{ position: 'absolute', top: 180, left: 720, right: 50 }}>
        <div style={{ display: 'flex', gap: 24 }}>
          {/* Component cards */}
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 26, color: 'rgba(255,255,255,0.4)', marginBottom: 14, letterSpacing: '0.1em', fontWeight: 600 }}>电路元件</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {p.components.map((c, i) => {
                const progress = spring({ frame: frame - compStart - i * 25, fps, from: 0, to: 1, config: { damping: 12, stiffness: 140 } });
                return (
                  <div
                    key={i}
                    style={{
                      padding: '18px 22px',
                      background: 'rgba(34,197,94,0.06)',
                      border: '1px solid rgba(34,197,94,0.25)',
                      borderRadius: 14,
                      opacity: progress,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
                      <span style={{ fontSize: 24, fontWeight: 800, color: '#22c55e' }}>{c.name}</span>
                      <span style={{ fontSize: 22, fontWeight: 700, color: '#06b6d4' }}>
                        {c.value}{c.unit}
                      </span>
                    </div>
                    <div style={{ fontSize: 20, color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>{c.description}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Measurements - vertical layout, right of components */}
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 26, color: 'rgba(255,255,255,0.4)', marginBottom: 14, letterSpacing: '0.1em', fontWeight: 600 }}>测量数据</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {p.measurements.map((m, i) => {
                const opacity = interpolate(frame, [measStart + i * 20, measStart + i * 20 + 18], [0, 1], { extrapolateRight: 'clamp' });
                return (
                  <div
                    key={i}
                    style={{
                      padding: '16px 20px',
                      background: `${m.color}12`,
                      border: `1px solid ${m.color}35`,
                      borderRadius: 12,
                      opacity,
                    }}
                  >
                    <div style={{ fontSize: 24, color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>{m.label}</div>
                    <div style={{ fontSize: 36, fontWeight: 900, color: m.color }}>
                      {m.value}<span style={{ fontSize: 20, opacity: 0.8, marginLeft: 4 }}>{m.unit}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Tips */}
        <div style={{ marginTop: -120 }}>
          <div style={{ fontSize: 26, color: 'rgba(255,255,255,0.4)', marginBottom: 12, fontWeight: 600 }}>💡 实用技巧</div>
          {p.tips.map((tip, i) => {
            const opacity = interpolate(frame, [tipStart + i * 22, tipStart + i * 22 + 18], [0, 1], { extrapolateRight: 'clamp' });
            return (
              <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 10, opacity }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#22c55e', marginTop: 12, flexShrink: 0 }} />
                <span style={{ fontSize: 22, color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>{tip}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Key fact */}
      <div style={{ position: 'absolute', bottom: 20, left: 50, right: 50, opacity: factOpacity }}>
        <div style={{ padding: '18px 28px', background: '#0c1e2e', border: '1px solid rgba(34,197,94,0.25)', borderRadius: 16, textAlign: 'center' }}>
          <span style={{ fontSize: 22, color: 'rgba(255,255,255,0.85)', lineHeight: 1.5 }}>💡 {p.keyFact}</span>
        </div>
      </div>

      {/* Progress */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, width: `${interpolate(frame, [0, 480], [0, 100], { extrapolateRight: 'clamp' })}%`, height: 3, background: '#22c55e' }} />
    </AbsoluteFill>
  );
};
